import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiService } from '@services/api.service';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  of,
  tap,
} from 'rxjs';
import { SubSink } from 'subsink';

@Component({
  selector: 'ew-general-financial-tool',
  templateUrl: './general.component.html',
  styleUrls: ['../financial-tool.component.scss'],
})
export class FinancialToolGeneralComponent implements OnInit, OnDestroy {
  @Input() type: 'agency' | 'school' = 'agency';
  @Input() tab!: string;
  @Input() showTitle: boolean = true;
  form!: FormGroup;
  currencies: string[] = ['CAD', 'USD', 'GBP', 'AUD', 'EUR'];
  currenciesIsWithdrawable: string[] = [];
  countries: any[] = [];
  paymentMethods: any[] = [];
  loading: boolean = false;
  private subs = new SubSink();
  private logger = console;

  constructor(private API: ApiService) {}

  ngOnInit(): void {
    this.form = new FormGroup({
      sourceCurrency: new FormControl('CAD', [Validators.required]),
      paymentAmount: new FormControl(1000, [Validators.required]),
      countryPayFrom: new FormControl(null, [Validators.required]),
      currencyPayFrom: new FormControl('CAD', [Validators.required]),
      paymentMethod: new FormControl(null, [Validators.required]),
    });

    this.fetchCountries();
    this.fetchCurrencies();

    const controlsToWatch = [
      'sourceCurrency',
      'paymentAmount',
      'countryPayFrom',
      'currencyPayFrom',
    ];

    controlsToWatch.forEach((controlName) => {
      this.subs.sink = this.fc[controlName].valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(() => {
          if (this.fc[controlName].touched) {
            this.getPaymentMethod();
          }
        });
    });

    this.subs.sink = this.fc['paymentMethod'].valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        this.loading = false;
      });
  }

  /**
   * returns the values ​​of the form
   * @author Martin Batun Tec.
   */
  get fv() {
    return this.form.value;
  }

  /**
   * returns the controls ​​of the form
   * @author Martin Batun Tec.
   */
  get fc() {
    return this.form.controls;
  }

  /**
   * returns the label for selector one of the form
   * @author Martin Batun Tec.
   */
  get labelOne(): string {
    return this.tab != 'send' ? 'You are requesting:' : 'You are sending:';
  }

  /**
   * returns the label for PaymentMethodSelect of the form
   * @author Martin Batun Tec.
   */
  get labelPaymentMethodSelect(): string {
    const label: any = {
      collect: 'The student is paying:',
      withdraw: 'The beneficiary is receiving:',
      send:
        this.type == 'agency'
          ? 'The institution receives:'
          : 'The provider receives:',
    };
    return label[this.tab] || '';
  }

  /**
   * returns the totals ​​taxes
   * @author Martin Batun Tec.
   */
  get totalTaxes(): number {
    return this.fv.paymentMethod?.taxes
      ? this.fv.paymentMethod.taxes.reduce(
          (acc: number, obj: { amount: string }) =>
            acc + parseFloat(obj.amount),
          0,
        )
      : 0;
  }

  /**
   * get the list of payment methods
   * @author Martin Batun Tec.
   */
  getPaymentMethod(): void {
    this.loading = true;

    let body: any = {
      paymentAmount: Number(this.fv.paymentAmount),
      sourceCurrency: this.fv.sourceCurrency,
    };

    if (this.tab === 'collect') {
      body.countryPayFrom = this.fv.countryPayFrom?.iso;
    } else {
      body.currencyPayFrom = this.fv.currencyPayFrom;
    }

    if (
      !body.countryPayFrom ||
      !body.paymentAmount ||
      (this.tab == 'collect' && !body.sourceCurrency) ||
      (this.tab != 'collect' && !body.currencyPayFrom)
    ) {
      return;
    }

    if (this.tab == 'collect') {
      this.fetchStudentPaymentQuote(body);
    } else {
      this.fetchFinancialToolQuote(body);
    }
  }

  /**
   * Fetches available countries for payment
   */
  private fetchCountries(): void {
    this.loading = true;

    this.subs.sink = this.API.get(
      'v1/public/country/?isAvailableToSendPayment=true',
    )
      .pipe(
        tap((result) => {
          if (result.records) {
            this.countries = result.records;
            this.logger.log(
              {
                level: 'info',
                message: 'Countries fetched successfully',
                payload: this.countries.length,
              },
              'FETCH_COUNTRIES | INFO',
            );
          }
        }),
        catchError((error) => {
          this.logger.log(
            {
              level: 'error',
              message: 'Error processing countries data',
              error: {
                message: error.message,
                stack: error.stack,
              },
            },
            'FETCH_COUNTRIES | ERROR',
          );
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe();
  }

  /**
   * Fetches available currencies for withdrawal
   */
  private fetchCurrencies(): void {
    this.subs.sink = this.API.get('v1/catalogs/currencies?isWithdrawable=true')
      .pipe(
        tap((result) => {
          if (result.records) {
            this.currenciesIsWithdrawable = result.records.map(
              (e: any) => e.currencyIso,
            );
            this.logger.log(
              {
                level: 'info',
                message: 'Currencies fetched successfully',
                payload: this.currenciesIsWithdrawable.length,
              },
              'FETCH_CURRENCIES | INFO',
            );
          }
        }),
        catchError((error) => {
          this.logger.log(
            {
              level: 'error',
              message: 'Error processing currencies data',
              error: {
                message: error.message,
                stack: error.stack,
              },
            },
            'FETCH_CURRENCIES | ERROR',
          );
          return of(null);
        }),
      )
      .subscribe();
  }

  /**
   * Fetches student payment quote
   * @param body Request body
   */
  private fetchStudentPaymentQuote(body: any): void {
    this.subs.sink = this.API.post('/v1/public/quote/student-payment/', body)
      .pipe(
        tap((result) => {
          this.paymentMethods = result.paymentMethods;
          this.paymentMethods.forEach((e: any) => {
            e.logo = `assets/${
              e.type.toLowerCase().includes('mastercard')
                ? 'mastercard'
                : e.type.toLowerCase().includes('visa')
                ? 'visa'
                : e.type.toLowerCase().includes('transfer')
                ? 'transfer'
                : 'transfer'
            }.svg`;
          });

          if (this.paymentMethods.length) {
            this.form.patchValue({
              paymentMethod: this.fv.paymentMethod
                ? this.paymentMethods.find(
                    (e: any) =>
                      e.paymentMethodId ==
                      this.fv.paymentMethod?.paymentMethodId,
                  ) || this.paymentMethods[0]
                : this.paymentMethods[0],
            });
          }

          this.logger.log(
            {
              level: 'info',
              message: 'Student payment quote fetched successfully',
              payload: {
                paymentMethodsCount: this.paymentMethods.length,
              },
            },
            'FETCH_STUDENT_PAYMENT_QUOTE | INFO',
          );
        }),
        catchError((error) => {
          this.logger.log(
            {
              level: 'error',
              message: 'Error processing student payment quote',
              error: {
                message: error.message,
                stack: error.stack,
              },
              payload: body,
            },
            'FETCH_STUDENT_PAYMENT_QUOTE | ERROR',
          );
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe();
  }

  /**
   * Fetches financial tool quote
   * @param body Request body
   */
  private fetchFinancialToolQuote(body: any): void {
    this.subs.sink = this.API.post('/v1/public/quote/financial-tool/', body)
      .pipe(
        tap((result) => {
          const { record } = result;
          this.form.patchValue({
            paymentMethod: record,
          });

          this.logger.log(
            {
              level: 'info',
              message: 'Financial tool quote fetched successfully',
              payload: record,
            },
            'FETCH_FINANCIAL_TOOL_QUOTE | INFO',
          );
        }),
        catchError((error) => {
          this.logger.log(
            {
              level: 'error',
              message: 'Error processing financial tool quote',
              error: {
                message: error.message,
                stack: error.stack,
              },
              payload: body,
            },
            'FETCH_FINANCIAL_TOOL_QUOTE | ERROR',
          );
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe();
  }

  /**
   * destroy the subscription.
   * @author Martin Batun Tec.
   */
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
